import React from "react";
import FilterField from "../../components/filters/fields/FilterField";
import FilterMultiSelect from "../../components/filters/fields/FilterMultiSelect";
import FilterSelect from "../../components/filters/fields/FilterSelect";
import { CompactDateInput } from "../../components/filters/config/jobsFilterUtils";
import formatGroupedValues from "../../components/filters/utils/formatGroupedValues";
import { formatAppDate } from "../../utils/appDateTime";

export const CLIENT_SCHEDULE_DATE_RANGE_OPTIONS = [
  { value: "1", label: "1 Day" },
  { value: "7", label: "7 Days" },
];

export const CLIENT_CHECKIN_STATUS_OPTIONS = [
  { value: "future", label: "Future" },
  { value: "late", label: "Late" },
  { value: "checkIn", label: "Checked in" },
  { value: "noshow", label: "No show" },
  { value: "signoffPending", label: "Pending signoff" },
  { value: "signoffComplete", label: "Signoff complete" },
];

function FieldWrapper({ fieldId, label, focusedFieldId, onFocusClear, children }) {
  return (
    <FilterField
      id={fieldId}
      label={label}
      focusedFieldId={focusedFieldId}
      onFocusClear={onFocusClear}
    >
      {children}
    </FilterField>
  );
}

export function buildClientScheduleFilterChips({
  selectedClients = [],
  clients = [],
  selectedDate,
  selectedDateRange,
  selectedCheckinStatuses = [],
}) {
  const chips = [];

  if (selectedClients.length > 0) {
    const labelForClientId = (id) => {
      const match = clients.find((c) => String(c.id) === String(id));
      return match?.name ? String(match.name).trim() : `#${id}`;
    };
    chips.push({
      key: "client",
      fieldId: "client",
      label: `Clients: ${formatGroupedValues(selectedClients, labelForClientId, 3)}`,
    });
  }

  if (selectedCheckinStatuses.length) {
    selectedCheckinStatuses.forEach((status) => {
      const opt = CLIENT_CHECKIN_STATUS_OPTIONS.find((o) => o.value === status);
      chips.push({
        key: "checkin",
        value: status,
        fieldId: "checkin",
        label: `Check In Status: ${opt?.label || status}`,
      });
    });
  }

  if (selectedDate) {
    const label = formatAppDate(`${selectedDate}T12:00:00Z`, {
      emptyValue: selectedDate,
    });
    chips.push({ key: "date", fieldId: "date", label: `Date: ${label}` });
  }

  if (selectedDateRange) {
    const opt = CLIENT_SCHEDULE_DATE_RANGE_OPTIONS.find(
      (o) => o.value === String(selectedDateRange),
    );
    chips.push({
      key: "range",
      fieldId: "range",
      label: `Date Range: ${opt?.label || `${selectedDateRange} Days`}`,
    });
  }

  return chips;
}

export default function ClientScheduleFilterFields({
  focusedFieldId,
  onFocusClear,
  showClientFilter = false,
  clients = [],
  selectedClients = [],
  setSelectedClients,
  selectedCheckinStatuses,
  setSelectedCheckinStatuses,
  selectedDate,
  setSelectedDate,
  selectedDateRange,
  setSelectedDateRange,
}) {
  const clientOptions = clients.map((c) => ({
    value: String(c.id),
    label: c.name,
  }));

  return (
    <>
      {showClientFilter ? (
        <FieldWrapper
          fieldId="client"
          label="Client"
          focusedFieldId={focusedFieldId}
          onFocusClear={onFocusClear}
        >
          <FilterMultiSelect
            id="schedule-client"
            options={clientOptions}
            value={selectedClients}
            onChange={setSelectedClients}
            placeholder="Select Client"
          />
        </FieldWrapper>
      ) : null}

      <FieldWrapper
        fieldId="checkin"
        label="Check In Status"
        focusedFieldId={focusedFieldId}
        onFocusClear={onFocusClear}
      >
        <FilterMultiSelect
          id="checkin"
          value={selectedCheckinStatuses}
          onChange={setSelectedCheckinStatuses}
          options={CLIENT_CHECKIN_STATUS_OPTIONS}
          placeholder="All statuses"
        />
      </FieldWrapper>

      <FieldWrapper
        fieldId="date"
        label="Date"
        focusedFieldId={focusedFieldId}
        onFocusClear={onFocusClear}
      >
        <CompactDateInput
          id="date"
          value={selectedDate}
          onChange={setSelectedDate}
        />
      </FieldWrapper>

      <FieldWrapper
        fieldId="range"
        label="Date Range"
        focusedFieldId={focusedFieldId}
        onFocusClear={onFocusClear}
      >
        <FilterSelect
          id="range"
          value={selectedDateRange}
          onChange={setSelectedDateRange}
          options={CLIENT_SCHEDULE_DATE_RANGE_OPTIONS}
          placeholder="Select range"
        />
      </FieldWrapper>
    </>
  );
}
